/**
 * Mobile Time Picker Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * iPhone-style time picker with scrollable hour and minute selectors
 */

import { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { TimeFormat } from "../../../../types";
import { convertTo12Hour, convertTo24Hour } from "../../../../utils/transactionUtils";

export interface MobileTimePickerProps {
  open: boolean;
  currentTime: string;
  isEditMode: boolean;
  timeWasEnabled: boolean;
  transactionType: "expense" | "income" | "transfer";
  onTimeChange: (time: string) => void;
  onClose: () => void;
  onDone: () => void;
  getCurrentTime: () => string;
  timeFormat?: TimeFormat; // "12h" or "24h", defaults to "24h"
}

export default function MobileTimePicker({
  open,
  currentTime,
  isEditMode,
  timeWasEnabled,
  transactionType,
  onTimeChange,
  onClose,
  onDone,
  getCurrentTime,
  timeFormat = "24h",
}: MobileTimePickerProps) {
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);
  const periodScrollRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const [centeredHour, setCenteredHour] = useState<string>("");
  const [centeredMinute, setCenteredMinute] = useState<string>("");
  const [centeredPeriod, setCenteredPeriod] = useState<"AM" | "PM">("AM");
  const is12Hour = timeFormat === "12h";

  // Auto-scroll to selected hour and minute when time picker opens
  useEffect(() => {
    if (!open || (!hourScrollRef.current && !minuteScrollRef.current)) return;

    // Use the actual time from form data
    // In edit mode, always use the time from transaction (never current time)
    // In create mode, use current time as fallback if empty
    const timeToUse = currentTime || (!isEditMode ? getCurrentTime() : "");
    
    if (is12Hour) {
      // Convert 24-hour to 12-hour format
      const { hour, minute, period } = convertTo12Hour(timeToUse);
      setCenteredHour(String(hour));
      setCenteredMinute(minute);
      setCenteredPeriod(period);
      
      // Scroll to selected hour (1-12, index 0-11)
      if (hourScrollRef.current && hour) {
        const hourIndex = hour - 1; // Convert 1-12 to 0-11
        const hourButton = hourScrollRef.current.children[hourIndex] as HTMLElement;
        if (hourButton) {
          setTimeout(() => {
            hourButton.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      }
      
      // Scroll to selected period (AM/PM)
      if (periodScrollRef.current) {
        const periodIndex = period === "AM" ? 0 : 1;
        const periodButton = periodScrollRef.current.children[periodIndex] as HTMLElement;
        if (periodButton) {
          setTimeout(() => {
            periodButton.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 200);
        }
      }
    } else {
      // 24-hour format
      const [hour = "00", minute = "00"] = timeToUse.split(":");
      setCenteredHour(hour);
      setCenteredMinute(minute);
      
      // Scroll to selected hour
      if (hourScrollRef.current && hour) {
        const hourIndex = parseInt(hour, 10);
        const hourButton = hourScrollRef.current.children[hourIndex] as HTMLElement;
        if (hourButton) {
          setTimeout(() => {
            hourButton.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      }
    }

    // Scroll to selected minute
    if (minuteScrollRef.current) {
      const minuteToUse = is12Hour ? convertTo12Hour(timeToUse).minute : timeToUse.split(":")[1] || "00";
      const minuteIndex = parseInt(minuteToUse, 10);
      const minuteButton = minuteScrollRef.current.children[minuteIndex] as HTMLElement;
      if (minuteButton) {
        setTimeout(() => {
          minuteButton.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
      }
    }
  }, [open, currentTime, isEditMode, getCurrentTime, is12Hour]);

  // Snap to nearest item on scroll end (iPhone-style)
  useEffect(() => {
    if (!open) return;

    const snapToNearest = (scrollContainer: HTMLElement, itemCount: number, type: "hour" | "minute" | "period") => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      let nearestIndex = -1;
      let minDistance = Infinity;

      for (let i = 0; i < scrollContainer.children.length; i++) {
        const child = scrollContainer.children[i] as HTMLElement;
        const childRect = child.getBoundingClientRect();
        const childCenterY = childRect.top + childRect.height / 2;
        const distance = Math.abs(childCenterY - centerY);

        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = i;
        }
      }

      if (nearestIndex >= 0) {
        const nearestButton = scrollContainer.children[nearestIndex] as HTMLElement;
        const timeToUse = currentTime || ((!isEditMode || (isEditMode && !timeWasEnabled)) ? getCurrentTime() : "00:00");

        if (type === "hour") {
          if (is12Hour) {
            // 12-hour format: index 0-11 represents hours 1-12
            const hour12 = nearestIndex + 1;
            setCenteredHour(String(hour12));
            const { minute, period } = convertTo12Hour(timeToUse);
            const newTime = convertTo24Hour(hour12, minute, period);
            onTimeChange(newTime);
          } else {
            // 24-hour format
            const value = String(nearestIndex).padStart(2, "0");
            setCenteredHour(value);
            const [, currentMinute = "00"] = timeToUse.split(":");
            const newTime = `${value}:${currentMinute}`;
            onTimeChange(newTime);
          }
        } else if (type === "minute") {
          const value = String(nearestIndex).padStart(2, "0");
          setCenteredMinute(value);
          if (is12Hour) {
            const { hour, period } = convertTo12Hour(timeToUse);
            const newTime = convertTo24Hour(hour, value, period);
            onTimeChange(newTime);
          } else {
            const [currentHour = "00"] = timeToUse.split(":");
            const newTime = `${currentHour}:${value}`;
            onTimeChange(newTime);
          }
        } else if (type === "period") {
          const period = nearestIndex === 0 ? "AM" : "PM";
          setCenteredPeriod(period);
          const { hour, minute } = convertTo12Hour(timeToUse);
          const newTime = convertTo24Hour(hour, minute, period);
          onTimeChange(newTime);
        }

        // Scroll after a small delay to ensure state is updated
        setTimeout(() => {
          nearestButton.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 10);
      }
    };

    let hourScrollTimeout: NodeJS.Timeout | null = null;
    let minuteScrollTimeout: NodeJS.Timeout | null = null;
    let periodScrollTimeout: NodeJS.Timeout | null = null;

    const handleHourScroll = () => {
      if (hourScrollTimeout) clearTimeout(hourScrollTimeout);
      hourScrollTimeout = setTimeout(() => {
        if (hourScrollRef.current) {
          snapToNearest(hourScrollRef.current, is12Hour ? 12 : 24, "hour");
        }
      }, 150);
    };

    const handleMinuteScroll = () => {
      if (minuteScrollTimeout) clearTimeout(minuteScrollTimeout);
      minuteScrollTimeout = setTimeout(() => {
        if (minuteScrollRef.current) {
          snapToNearest(minuteScrollRef.current, 60, "minute");
        }
      }, 150);
    };

    const handlePeriodScroll = () => {
      if (periodScrollTimeout) clearTimeout(periodScrollTimeout);
      periodScrollTimeout = setTimeout(() => {
        if (periodScrollRef.current) {
          snapToNearest(periodScrollRef.current, 2, "period");
        }
      }, 150);
    };

    if (hourScrollRef.current) {
      hourScrollRef.current.addEventListener("scroll", handleHourScroll);
    }
    if (minuteScrollRef.current) {
      minuteScrollRef.current.addEventListener("scroll", handleMinuteScroll);
    }
    if (periodScrollRef.current && is12Hour) {
      periodScrollRef.current.addEventListener("scroll", handlePeriodScroll);
    }

    return () => {
      if (hourScrollTimeout) clearTimeout(hourScrollTimeout);
      if (minuteScrollTimeout) clearTimeout(minuteScrollTimeout);
      if (periodScrollTimeout) clearTimeout(periodScrollTimeout);
      if (hourScrollRef.current) {
        hourScrollRef.current.removeEventListener("scroll", handleHourScroll);
      }
      if (minuteScrollRef.current) {
        minuteScrollRef.current.removeEventListener("scroll", handleMinuteScroll);
      }
      if (periodScrollRef.current) {
        periodScrollRef.current.removeEventListener("scroll", handlePeriodScroll);
      }
    };
  }, [open, currentTime, isEditMode, timeWasEnabled, transactionType, onTimeChange, getCurrentTime, is12Hour]);

  if (!open) return null;

  const timeToUse = currentTime || ((!isEditMode || (isEditMode && !timeWasEnabled)) ? getCurrentTime() : "00:00");
  
  // Get current values based on format
  let currentHour: string;
  let currentMinute: string;
  let currentPeriod: "AM" | "PM" = "AM";
  
  if (is12Hour) {
    const { hour, minute, period } = convertTo12Hour(timeToUse);
    currentHour = String(hour);
    currentMinute = minute;
    currentPeriod = period;
  } else {
    [currentHour = "00", currentMinute = "00"] = timeToUse.split(":");
  }

  return (
    <>
      <Paper
        ref={timePickerRef}
        elevation={0}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(20px + env(safe-area-inset-bottom))",
          width: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          boxSizing: "border-box",
          zIndex: 1300,
          border: "none",
          borderTop: "1px solid",
          borderColor: "divider",
          boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: "400px",
          pointerEvents: "auto",
          WebkitTransform: "translate3d(0, 0, 0)",
          transform: "translate3d(0, 0, 0)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", p: 2, width: "100%" }}>
          {/* Time Picker Header */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "medium",
              fontSize: "1rem",
              textAlign: "center",
              mb: 2,
            }}
          >
            Select Time
          </Typography>

          {/* Time Display and Controls - iPhone Style */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start", gap: 1, mb: 2 }}>
            {/* Hours */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                flex: 1,
                position: "relative",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: "medium" }}>
                Hour
              </Typography>
              {/* Fixed container with fixed blue indicator */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "200px",
                  overflow: "hidden",
                }}
              >
                {/* Scrollable hours - only this moves */}
                <Box
                  ref={hourScrollRef}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    height: "100%",
                    overflowY: "auto",
                    width: "100%",
                    alignItems: "center",
                    pt: "72px", // Padding to center first item: (200px - 56px) / 2
                    pb: "72px", // Padding to center last item
                    zIndex: 0,
                    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {Array.from({ length: is12Hour ? 12 : 24 }, (_, i) => {
                    const hourValue = is12Hour ? i + 1 : i; // 1-12 for 12h, 0-23 for 24h
                    const hour = is12Hour ? String(hourValue) : String(i).padStart(2, "0");
                    const isSelected = hour === currentHour;
                    const isCentered = hour === centeredHour && centeredHour !== "";
                    const shouldHighlight = isSelected || isCentered;

                    return (
                      <Button
                        key={hour}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (is12Hour) {
                            const { minute, period } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hourValue, minute, period);
                            setCenteredHour(hour);
                            onTimeChange(newTime);
                          } else {
                            const [, minute = "00"] = timeToUse.split(":");
                            const newTime = `${hour}:${minute}`;
                            setCenteredHour(hour);
                            onTimeChange(newTime);
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          setCenteredHour(hour);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (is12Hour) {
                            const { minute, period } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hourValue, minute, period);
                            setCenteredHour(hour);
                            onTimeChange(newTime);
                          } else {
                            const [, minute = "00"] = timeToUse.split(":");
                            const newTime = `${hour}:${minute}`;
                            setCenteredHour(hour);
                            onTimeChange(newTime);
                          }
                        }}
                        sx={{
                          minWidth: "70px",
                          width: "70px",
                          height: "56px",
                          py: 0,
                          fontSize: shouldHighlight ? "1.3rem" : "1rem",
                          fontWeight: shouldHighlight ? "bold" : "normal",
                          backgroundColor: "transparent",
                          color: shouldHighlight ? "primary.main" : "text.primary",
                          borderRadius: shouldHighlight ? "28px" : 2,
                          border: shouldHighlight ? "2px solid" : "1px solid",
                          borderColor: shouldHighlight ? "primary.main" : "transparent",
                          position: "relative",
                          zIndex: 2,
                          visibility: "visible",
                          opacity: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            borderColor: shouldHighlight ? "primary.main" : "divider",
                          },
                        }}
                      >
                        {hour}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Minutes */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                flex: 1,
                position: "relative",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: "medium" }}>
                Minute
              </Typography>
              {/* Fixed container */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "200px",
                  overflow: "hidden",
                }}
              >
                {/* Scrollable minutes - only this moves */}
                <Box
                  ref={minuteScrollRef}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    height: "100%",
                    overflowY: "auto",
                    width: "100%",
                    alignItems: "center",
                    pt: "72px", // Padding to center first item: (200px - 56px) / 2
                    pb: "72px", // Padding to center last item
                    zIndex: 0,
                    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {Array.from({ length: 60 }, (_, i) => {
                    const minute = String(i).padStart(2, "0");
                    const isSelected = minute === currentMinute;
                    const isCentered = minute === centeredMinute && centeredMinute !== "";
                    const shouldHighlight = isSelected || isCentered;

                    return (
                      <Button
                        key={minute}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (is12Hour) {
                            const { hour, period } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hour, minute, period);
                            setCenteredMinute(minute);
                            onTimeChange(newTime);
                          } else {
                            const [hour = "00"] = timeToUse.split(":");
                            const newTime = `${hour}:${minute}`;
                            setCenteredMinute(minute);
                            onTimeChange(newTime);
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          setCenteredMinute(minute);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (is12Hour) {
                            const { hour, period } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hour, minute, period);
                            setCenteredMinute(minute);
                            onTimeChange(newTime);
                          } else {
                            const [hour = "00"] = timeToUse.split(":");
                            const newTime = `${hour}:${minute}`;
                            setCenteredMinute(minute);
                            onTimeChange(newTime);
                          }
                        }}
                        sx={{
                          minWidth: "70px",
                          width: "70px",
                          height: "56px",
                          py: 0,
                          fontSize: shouldHighlight ? "1.3rem" : "1rem",
                          fontWeight: shouldHighlight ? "bold" : "normal",
                          backgroundColor: "transparent",
                          color: shouldHighlight ? "primary.main" : "text.primary",
                          borderRadius: shouldHighlight ? "28px" : 2,
                          border: shouldHighlight ? "2px solid" : "1px solid",
                          borderColor: shouldHighlight ? "primary.main" : "transparent",
                          position: "relative",
                          zIndex: 2,
                          visibility: "visible",
                          opacity: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            borderColor: shouldHighlight ? "primary.main" : "divider",
                          },
                        }}
                      >
                        {minute}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* AM/PM Selector (only for 12-hour format) */}
            {is12Hour && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  flex: 0.8,
                  position: "relative",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: "medium" }}>
                  Period
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "200px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    ref={periodScrollRef}
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      height: "100%",
                      overflowY: "auto",
                      width: "100%",
                      alignItems: "center",
                      pt: "72px",
                      pb: "72px",
                      zIndex: 0,
                      WebkitOverflowScrolling: "touch",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    {(["AM", "PM"] as const).map((period) => {
                      const isSelected = period === currentPeriod;
                      const isCentered = period === centeredPeriod;
                      const shouldHighlight = isSelected || isCentered;

                      return (
                        <Button
                          key={period}
                          onClick={(e) => {
                            e.stopPropagation();
                            const { hour, minute } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hour, minute, period);
                            setCenteredPeriod(period);
                            onTimeChange(newTime);
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            setCenteredPeriod(period);
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const { hour, minute } = convertTo12Hour(timeToUse);
                            const newTime = convertTo24Hour(hour, minute, period);
                            setCenteredPeriod(period);
                            onTimeChange(newTime);
                          }}
                          sx={{
                            minWidth: "70px",
                            width: "70px",
                            height: "56px",
                            py: 0,
                            fontSize: shouldHighlight ? "1.3rem" : "1rem",
                            fontWeight: shouldHighlight ? "bold" : "normal",
                            backgroundColor: "transparent",
                            color: shouldHighlight ? "primary.main" : "text.primary",
                            borderRadius: shouldHighlight ? "28px" : 2,
                            border: shouldHighlight ? "2px solid" : "1px solid",
                            borderColor: shouldHighlight ? "primary.main" : "transparent",
                            position: "relative",
                            zIndex: 2,
                            visibility: "visible",
                            opacity: 1,
                            "&:hover": {
                              backgroundColor: "action.hover",
                              borderColor: shouldHighlight ? "primary.main" : "divider",
                            },
                          }}
                        >
                          {period}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Done Button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onDone}
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: "bold",
            }}
          >
            Done
          </Button>
        </Box>
      </Paper>
      {/* Bottom offset area with same background color */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: "calc(20px + env(safe-area-inset-bottom))",
          bgcolor: "background.paper",
          zIndex: 1299,
          borderTop: "1px solid",
          borderColor: "background.paper",
        }}
      />
    </>
  );
}
